import React, { useState } from "react";
import { T } from "../theme";
import { ENGAGEMENT_TYPES, typeKey, typeLabel, REGIONS, INVOICING, CURRENCIES } from "../finance/presets";
import validateConfig from "../finance/validateConfig";
import { toNative, requiredProblems, seed, newRosterId, startNewBlock, MODULE_LABELS, MODULE_ORDER } from "./configForm";
import { card, h3, hint, btn, Field, Row, Text, Num, Select, RowControls, move, without, patchAt } from "./fields";

// Setup: the agreement as a form. Explicit Save; nothing here debounces.
const STATUSES = ["Pre-engagement", "Activation", "In Flight", "Complete"];
const clone = v => JSON.parse(JSON.stringify(v));

export default function SetupForm({ eng, form, setForm, dirty }) {
  const f = eng.engagement.fields;
  const [meta, setMeta] = useState({ status: f.Status || "", type: typeKey(f["Engagement Type"]), logo: f["Logo URL"] || "" });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const metaDirty = meta.status !== (f.Status || "") || meta.type !== typeKey(f["Engagement Type"]) || meta.logo !== (f["Logo URL"] || "");
  const anyDirty = dirty || metaDirty;

  const update = (fn) => setForm(prev => { const base = clone(prev || form); fn(base); return base; });
  const setTop = (k, v) => update(d => { d.top[k] = v; });
  const setMod = (k, patch) => update(d => { d.modules[k] = { ...d.modules[k], ...patch }; });
  const toggle = (k, on) => {
    if (!on && !window.confirm(`Turn off ${MODULE_LABELS[k]}? Its settings are removed from the config (stored monthly facts stay but stop rendering).`)) return;
    update(d => { if (on) d.modules[k] = seed(k, eng.today, d.top.currency); else delete d.modules[k]; });
  };

  const native = toNative(form);
  const problems = [...requiredProblems(form), ...validateConfig(native)];
  const save = async () => {
    if (problems.length) return;
    setSaving(true);
    const extra = {};
    if (meta.status !== (f.Status || "")) extra["Status"] = meta.status;
    if (meta.type !== typeKey(f["Engagement Type"]) || (f["Engagement Type"] && f["Engagement Type"] !== meta.type)) extra["Engagement Type"] = meta.type; // also rewrites a legacy key
    if (meta.logo !== (f["Logo URL"] || "")) extra["Logo URL"] = meta.logo;
    // The type is written into the config when it changed or the config already carried one.
    const cfgOut = { ...native, ...(meta.type && (meta.type !== typeKey(f["Engagement Type"]) || native.engagementType) ? { engagementType: meta.type } : {}) };
    const r = await eng.saveConfig(cfgOut, extra);
    setSaving(false);
    if (r === "saved") { setForm(null); setSaved(true); setTimeout(() => setSaved(false), 2500); }
  };
  const discard = () => { setForm(null); setMeta({ status: f.Status || "", type: f["Engagement Type"] || "", logo: f["Logo URL"] || "" }); };
  const doStartNewBlock = async () => {
    if (dirty) { window.alert("Save or discard your setup changes first."); return; }
    const b = eng.cfg.modules.sessionBlock;
    if (!window.confirm(`Start block ${(b.blockNo || 1) + 1}? Block ${b.blockNo || 1}'s sessions and invoices move to history and the new block opens today.`)) return;
    const { config, data } = startNewBlock(eng.rawConfig, { sessions: eng.blockSessions, history: eng.blockHistory }, eng.today);
    await eng.saveConfig(config, {}, data);
  };

  const m = form.modules;
  const presetHint = meta.type && ENGAGEMENT_TYPES[meta.type] ? ENGAGEMENT_TYPES[meta.type].modules.filter(x => x !== "progressPhases").map(x => MODULE_LABELS[x] || x).join(", ") : null;
  const setRegion = (region) => update(d => { const r = REGIONS[region]; d.top.region = region; d.top.currency = r.currency; d.top.tax = r.tax; d.top.invoicing = r.invoicing; });

  return (
    <div>
      <div style={{ position: "sticky", top: 0, zIndex: 2, background: T.bgSurface, padding: "6px 0 12px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div style={{ fontSize: 13, color: problems.length ? T.statusOff : T.fgMuted }}>
          {problems.length ? `${problems.length} thing${problems.length === 1 ? "" : "s"} to fix before saving` : anyDirty ? "Unsaved changes" : saved ? "Saved" : "Everything saved"}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={discard} disabled={!anyDirty} style={{ ...btn(false), opacity: anyDirty ? 1 : 0.5 }}>Discard</button>
          <button onClick={save} disabled={!anyDirty || saving || problems.length > 0 || eng.readOnly.includes("Finance Config")} style={{ ...btn(true), opacity: anyDirty && !problems.length ? 1 : 0.5 }}>{saving ? "Saving…" : "Save setup"}</button>
        </div>
      </div>
      {problems.length > 0 && <ul style={{ margin: "0 0 14px", paddingLeft: 18, fontSize: 12, color: T.statusOff }}>{problems.map(p => <li key={p}>{p}</li>)}</ul>}

      <div style={card}>
        <h3 style={h3}>Engagement</h3>
        <Row>
          <Field label="Type"><Select value={meta.type} onChange={v => setMeta(x => ({ ...x, type: v }))} options={[["", "—"], ...Object.entries(ENGAGEMENT_TYPES).map(([k, t]) => [k, t.label])]} /></Field>
          <Field label="Status"><Select value={meta.status} onChange={v => setMeta(x => ({ ...x, status: v }))} options={[["", "—"], ...STATUSES.map(s => [s, s])]} /></Field>
          <Field label="Start month"><Text value={form.top.engagementStart} onChange={v => setTop("engagementStart", v)} placeholder="YYYY-MM" /></Field>
        </Row>
        <Row>
          <Field label="Look-ahead months" width="160px"><Num value={form.top.ledgerLookAhead} onChange={v => setTop("ledgerLookAhead", v === "" ? 0 : v)} min="0" /></Field>
          <Field label="Logo URL"><Text value={meta.logo} onChange={v => setMeta(x => ({ ...x, logo: v }))} placeholder="https://…" /></Field>
        </Row>
        {presetHint && <p style={hint}>A {typeLabel(meta.type).toLowerCase()} usually has: {presetHint}. Coaching is sessions sold as a block; a programme is a monthly retainer with coaching around it. The type is a label; switch modules on below.</p>}
      </div>

      <div style={card}>
        <h3 style={h3}>Billing</h3>
        <p style={hint}>Choosing the region fills the rest; each can be changed. New Zealand: NZD, plus GST, invoiced in Xero. International: the client's currency, no GST, invoiced from the Hub and keyed into Xero once paid.</p>
        <Row>
          <Field label="Region" width="180px"><Select value={form.top.region} onChange={setRegion} options={Object.entries(REGIONS).map(([k, r]) => [k, r.label])} /></Field>
          <Field label="Currency" width="120px"><Select value={form.top.currency} onChange={v => setTop("currency", v)} options={CURRENCIES.map(c => [c, c])} /></Field>
          <Field label="Tax" width="140px"><Select value={form.top.tax} onChange={v => setTop("tax", v)} options={[["gst", "+ GST"], ["none", "No GST"]]} /></Field>
          <Field label="Invoices raised in"><Select value={form.top.invoicing} onChange={v => setTop("invoicing", v)} options={Object.entries(INVOICING)} /></Field>
        </Row>
      </div>

      <div style={card}>
        <h3 style={h3}>Invoicing</h3>
        <p style={hint}>Who the generated invoice is addressed to, and the short name in its file name. Used by Generate invoice (USD engagements). Nothing here renders on the client page.</p>
        <Row>
          <Field label="Bill to · name"><Text value={form.invoicing.billTo.name} onChange={v => update(d => { d.invoicing.billTo.name = v; })} placeholder="The person the invoice is addressed to" /></Field>
          <Field label="Bill to · email"><Text value={form.invoicing.billTo.email} onChange={v => update(d => { d.invoicing.billTo.email = v; })} /></Field>
          <Field label="Short name (file name)" width="200px"><Text value={form.invoicing.clientShort} onChange={v => update(d => { d.invoicing.clientShort = v; })} placeholder="e.g. Keynes" /></Field>
        </Row>
        <Row>
          <Field label="Bill to · address"><Text value={form.invoicing.billTo.address} onChange={v => update(d => { d.invoicing.billTo.address = v; })} placeholder="Street, city, state, postcode, country" /></Field>
        </Row>
      </div>

      {MODULE_ORDER.map(k => (
        <div key={k} style={card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
            <h3 style={h3}>{MODULE_LABELS[k]}</h3>
            <label style={{ fontSize: 12, color: T.fgSecondary, display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}><input type="checkbox" checked={!!m[k]} onChange={e => toggle(k, e.target.checked)} /> On</label>
          </div>
          {m[k] && <div style={{ marginTop: 14 }}>
            {k === "retainer" && <Retainer mod={m.retainer} set={p => setMod("retainer", p)} />}
            {k === "recurringSessions" && <Coaching mod={m.recurringSessions} set={p => setMod("recurringSessions", p)} />}
            {k === "drawdownPool" && <Row>
              <Field label="Label"><Text value={m.drawdownPool.label} onChange={v => setMod("drawdownPool", { label: v })} placeholder="Strategy draw-down" /></Field>
              <Field label="Monthly accrual" width="140px"><Num value={m.drawdownPool.monthlyAccrual} onChange={v => setMod("drawdownPool", { monthlyAccrual: v })} /></Field>
              <Field label="Opening balance" width="140px"><Num value={m.drawdownPool.openingBalance} onChange={v => setMod("drawdownPool", { openingBalance: v === "" ? undefined : v })} /></Field>
            </Row>}
            {k === "rateCard" && <RateCard mod={m.rateCard} set={p => setMod("rateCard", p)} />}
            {k === "sessionBlock" && <Block mod={m.sessionBlock} set={p => setMod("sessionBlock", p)} onStartNew={doStartNewBlock} dirty={dirty} />}
          </div>}
        </div>
      ))}
    </div>
  );
}

function Retainer({ mod, set }) {
  const inc = mod.includes || [];
  return (
    <>
      <Row>
        <Field label="Label"><Text value={mod.label} onChange={v => set({ label: v })} /></Field>
        <Field label="Headline amount" width="140px"><Num value={mod.amount} onChange={v => set({ amount: v })} /></Field>
        <Field label="Monthly total (ex tax)" width="160px"><Num value={mod.monthlyTotal} onChange={v => set({ monthlyTotal: v })} /></Field>
      </Row>
      <div style={{ fontSize: 11, fontWeight: 600, color: T.fgSecondary, textTransform: "uppercase", letterSpacing: "0.05em", margin: "6px 0" }}>Included monthly</div>
      {inc.map((x, i) => (
        <Row key={i} align="center">
          <Text value={x.label} onChange={v => set({ includes: patchAt(inc, i, { label: v }) })} placeholder="What" />
          <Text value={x.description} onChange={v => set({ includes: patchAt(inc, i, { description: v }) })} placeholder="Detail" />
          <Select value={x.roster || ""} onChange={v => set({ includes: patchAt(inc, i, v ? { roster: v } : { roster: undefined }) })} options={[["", "Expands to: nobody"], ["leadership", "Leadership roster"], ["team", "Team roster"]]} />
          <RowControls onUp={() => set({ includes: move(inc, i, -1) })} onDown={() => set({ includes: move(inc, i, 1) })} onDelete={() => set({ includes: without(inc, i) })} />
        </Row>
      ))}
      <button type="button" onClick={() => set({ includes: [...inc, { label: "", description: "" }] })} style={btn(false)}>+ Add included line</button>
    </>
  );
}

function Coaching({ mod, set }) {
  const seats = mod.seats || [];
  const roster = mod.sessions || [];
  const [newName, setNewName] = useState("");
  const addPerson = () => {
    if (!newName.trim()) return;
    set({ sessions: [...roster, { id: newRosterId(newName, roster), label: newName.trim(), type: "leadership", status: "active" }] });
    setNewName("");
  };
  return (
    <>
      <Row>
        <Field label="Mode" width="200px"><Select value={mod.mode || "sessions"} onChange={v => set(v === "seats" ? { mode: "seats", seats: seats.length ? seats : [{ type: "leadership", label: "Leadership coaching", count: 0, rate: 0 }, { type: "team", label: "Team coaching", count: 0, rate: 0 }] } : { mode: undefined })} options={[["sessions", "Per-person sessions (Happened / Didn't)"], ["seats", "Seats included in the retainer"]]} /></Field>
        <Field label="Rollover per missed session" width="180px"><Num value={mod.rolloverValue} onChange={v => set({ rolloverValue: v })} /></Field>
      </Row>
      {mod.mode === "seats" && <>
        <div style={{ fontSize: 11, fontWeight: 600, color: T.fgSecondary, textTransform: "uppercase", letterSpacing: "0.05em", margin: "6px 0" }}>Seats</div>
        {seats.map((s, i) => (
          <Row key={i} align="center">
            <Select value={s.type} onChange={v => set({ seats: patchAt(seats, i, { type: v }) })} options={[["leadership", "Leadership"], ["team", "Team"]]} />
            <Text value={s.label} onChange={v => set({ seats: patchAt(seats, i, { label: v }) })} placeholder="Label" />
            <Num value={s.count} onChange={v => set({ seats: patchAt(seats, i, { count: v }) })} placeholder="Count" />
            <Num value={s.rate} onChange={v => set({ seats: patchAt(seats, i, { rate: v }) })} placeholder="Rate per seat" />
            <RowControls onUp={() => set({ seats: move(seats, i, -1) })} onDown={() => set({ seats: move(seats, i, 1) })} onDelete={() => set({ seats: without(seats, i) })} />
          </Row>
        ))}
        <button type="button" onClick={() => set({ seats: [...seats, { type: "team", label: "", count: 0, rate: 0 }] })} style={{ ...btn(false), marginBottom: 14 }}>+ Add seat line</button>
      </>}
      <div style={{ fontSize: 11, fontWeight: 600, color: T.fgSecondary, textTransform: "uppercase", letterSpacing: "0.05em", margin: "6px 0" }}>Roster · {roster.length}</div>
      <div className="roster-head" style={{ display: "grid", gridTemplateColumns: "minmax(140px,1.4fr) 110px 110px minmax(120px,1fr) 90px 90px auto", gap: 8, fontSize: 11, color: T.fgMuted, padding: "0 0 4px" }}><span>Name</span><span>Type</span><span>Status</span><span>Coach</span><span>From</span><span>To</span><span></span></div>
      {roster.map((p, i) => (
        <div key={p.id} className="roster-row" style={{ display: "grid", gridTemplateColumns: "minmax(140px,1.4fr) 110px 110px minmax(120px,1fr) 90px 90px auto", gap: 8, alignItems: "center", marginBottom: 6 }}>
          <span><Text value={p.label} onChange={v => set({ sessions: patchAt(roster, i, { label: v }) })} /><span style={{ fontSize: 10, color: T.fgMuted }}>id {p.id}</span></span>
          <Select value={p.type || ""} onChange={v => set({ sessions: patchAt(roster, i, v ? { type: v } : { type: undefined }) })} options={[["", "—"], ["leadership", "Leadership"], ["team", "Team"]]} />
          <Select value={p.status || "active"} onChange={v => set({ sessions: patchAt(roster, i, { status: v }) })} options={[["active", "Active"], ["paused", "Paused"], ["optedOut", "Opted out"], ["ended", "Ended"]]} />
          <Text value={p.coach} onChange={v => set({ sessions: patchAt(roster, i, { coach: v || undefined }) })} placeholder="Coach" />
          <Text value={p.from} onChange={v => set({ sessions: patchAt(roster, i, { from: v || undefined }) })} placeholder="YYYY-MM" />
          <Text value={p.to} onChange={v => set({ sessions: patchAt(roster, i, { to: v || undefined }) })} placeholder="YYYY-MM" />
          <RowControls onUp={() => set({ sessions: move(roster, i, -1) })} onDown={() => set({ sessions: move(roster, i, 1) })} onDelete={() => { if (window.confirm(`Remove ${p.label} from the roster? Past months keep their history.`)) set({ sessions: without(roster, i) }); }} />
        </div>
      ))}
      <Row align="center">
        <Text value={newName} onChange={setNewName} placeholder="New person's name" onKeyDown={e => e.key === "Enter" && addPerson()} />
        <button type="button" onClick={addPerson} style={btn(false)}>+ Add to roster</button>
      </Row>
    </>
  );
}

function RateCard({ mod, set }) {
  const rates = mod.rates || [];
  const nextId = () => { let n = rates.length + 1; while (rates.some(r => r.id === `r${n}`)) n++; return `r${n}`; };
  return (
    <>
      {rates.map((r, i) => (
        <Row key={r.id || i} align="center">
          <Text value={r.label} onChange={v => set({ rates: patchAt(rates, i, { label: v }) })} placeholder="Label" />
          <Num value={r.rate} onChange={v => set({ rates: patchAt(rates, i, { rate: v }) })} placeholder="Rate" />
          <Text value={r.unit} onChange={v => set({ rates: patchAt(rates, i, { unit: v || undefined }) })} placeholder="/hr" />
          <RowControls onUp={() => set({ rates: move(rates, i, -1) })} onDown={() => set({ rates: move(rates, i, 1) })} onDelete={() => set({ rates: without(rates, i) })} />
        </Row>
      ))}
      <button type="button" onClick={() => set({ rates: [...rates, { id: nextId(), label: "", rate: 0 }] })} style={btn(false)}>+ Add rate</button>
    </>
  );
}

function Block({ mod, set, onStartNew, dirty }) {
  return (
    <>
      <Row>
        <Field label="Label"><Text value={mod.label} onChange={v => set({ label: v })} /></Field>
        <Field label="Sessions purchased" width="140px"><Num value={mod.purchased} onChange={v => set({ purchased: v })} min="1" /></Field>
        <Field label="Period" ><Text value={mod.period} onChange={v => set({ period: v || undefined })} placeholder="e.g. Aug 2026 – Jan 2027" /></Field>
      </Row>
      <Row>
        <Field label="Rate per session" width="140px"><Num value={mod.rate} onChange={v => set({ rate: v === "" ? undefined : v })} /></Field>
        <Field label="or block value" width="140px"><Num value={mod.value} onChange={v => set({ value: v === "" ? undefined : v })} /></Field>
        <Field label="Block number" width="110px"><input value={mod.blockNo || 1} readOnly style={{ padding: "8px 10px", fontSize: 13, border: `1px solid ${T.borderSubtle}`, borderRadius: T.radiusSm, background: T.bgNeutral, color: T.fgSecondary, fontFamily: T.fontSans }} /></Field>
        <Field label="Opened" width="130px"><input value={mod.opened || ""} readOnly style={{ padding: "8px 10px", fontSize: 13, border: `1px solid ${T.borderSubtle}`, borderRadius: T.radiusSm, background: T.bgNeutral, color: T.fgSecondary, fontFamily: T.fontSans }} /></Field>
      </Row>
      <button type="button" onClick={onStartNew} style={btn(false)} title={dirty ? "Save or discard first" : ""}>Start new block</button>
      <p style={hint}>Moves this block's sessions and invoices into history and opens block {(mod.blockNo || 1) + 1} today. Block invoices are edited under Invoices.</p>
    </>
  );
}
